import { A11yModule } from '@angular/cdk/a11y';
import {
  Component,
  DestroyRef,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  computed,
  inject,
  viewChild,
  ChangeDetectionStrategy,
  AfterViewInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { Router, RouterOutlet } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { distinctUntilChanged } from 'rxjs/operators';
import { IntercomService } from '@core/providers/backend/intercom.service';
import { UserService } from '@core/providers/user/user.service';
import { LayoutUtil } from '@core/utils';
import { AuthService } from '@features/login/auth.service';
import { GlobalHeaderComponent } from './global-header/global-header.component';
import { isNavigationEnd } from './global-header/global-header.utils';
import { NavRailComponent } from './nav-rail/nav-rail.component';
import { ShellNavService } from './shell-nav.service';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    A11yModule,
    MatIcon,
    TranslatePipe,
    NavRailComponent,
    GlobalHeaderComponent,
    RouterOutlet,
  ],
  host: {
    '(document:keydown.escape)': 'closeRail()',
  },
})
export class LayoutComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly scrollContainer = viewChild<ElementRef<HTMLElement>>('scrollContainer');
  private readonly scrollSentinel = viewChild<ElementRef<HTMLElement>>('scrollSentinel');

  private layoutUtil = inject(LayoutUtil);
  private intercomService = inject(IntercomService);
  private userService = inject(UserService);
  private authService = inject(AuthService);
  private ngZone = inject(NgZone);
  private destroyRef = inject(DestroyRef);
  private readonly shellNav = inject(ShellNavService);
  private readonly router = inject(Router);

  isHeaderElevated = false;

  readonly isCompact = this.shellNav.isCompact;
  readonly isRailOpen = this.shellNav.isRailOpen;
  readonly launcherInPage = this.shellNav.launcherInPage;

  /** Only the summoned rail slides in; the permanent one must not animate on every page load. */
  readonly railEnterClass = computed(() => (this.isCompact() ? 'fb-rail-shell--enter' : ''));

  private scrollIntersectionObserver: IntersectionObserver | null = null;
  private lastPath: string | null = null;

  routeChanged() {
    this.intercomService.updateIntercom();
    this.layoutUtil.setIsSidebarExpanded(false);
  }

  openRail(): void {
    this.shellNav.openRail();
  }

  closeRail(): void {
    this.shellNav.closeRail();
  }

  ngOnInit(): void {
    if (this.authService.authenticated()) {
      this.userService.setLoggedIn(true);
    }

    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      if (!isNavigationEnd(event)) {
        return;
      }

      this.scrollToTopOnNewPage(event.urlAfterRedirects);
    });

    this.updateIntercom();
  }

  ngAfterViewInit(): void {
    const scrollRoot = this.scrollContainer()?.nativeElement;
    const sentinel = this.scrollSentinel()?.nativeElement;

    if (!scrollRoot || !sentinel) {
      return;
    }

    this.scrollIntersectionObserver?.disconnect();
    this.scrollIntersectionObserver = new IntersectionObserver(
      entries => {
        const entry = entries.at(0);
        const elevated = !(entry?.isIntersecting ?? true);

        if (elevated === this.isHeaderElevated) {
          return;
        }

        // IntersectionObserver runs outside Angular; re-enter only when value changes.
        this.ngZone.run(() => {
          this.isHeaderElevated = elevated;
        });
      },
      {
        root: scrollRoot,
        threshold: 0,
      }
    );

    this.scrollIntersectionObserver.observe(sentinel);
  }

  ngOnDestroy(): void {
    this.scrollIntersectionObserver?.disconnect();
    this.scrollIntersectionObserver = null;
  }

  /**
   * The page scrolls inside the shell rather than the window, so the router's own scroll reset
   * never reaches it. Filters rewrite the query string as they apply, so only a new path counts.
   */
  private scrollToTopOnNewPage(url: string): void {
    const path = url.split(/[?#]/)[0];
    const scrollRoot = this.scrollContainer()?.nativeElement;

    if (this.lastPath !== null && path !== this.lastPath && scrollRoot) {
      scrollRoot.scrollTop = 0;
    }

    this.lastPath = path;
  }

  private updateIntercom() {
    this.userService.isLoggedIn$
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(isLoggedIn => {
        if (isLoggedIn) {
          this.intercomService.updateIntercomData({
            user_id: this.userService.getUserUuid(),
            email: this.userService.getUserEmail(),
          });
        } else {
          this.intercomService.shutdownIntercom();
        }
      });
  }
}
